import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from 'api';
import { DataRoomFileBufferPreview } from 'models/File';
import { SoftSignal } from 'models/Lead';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { MINUTE } from 'data/global';

export const FILE_BUFFER_PREVIEW_QUERY_KEY = 'file-buffer-preview';
const PREVIEW_CACHE_TIME = 30 * MINUTE; // 30 min

type UseFileBufferPreviewQueryProps = {
  nodeId: string;
  isCurrentlyActiveNode: boolean;
  version?: number;
  enabled?: boolean;
};

export default function useFileBufferPreviewQuery({
  nodeId,
  isCurrentlyActiveNode,
  version,
  enabled = true,
}: UseFileBufferPreviewQueryProps) {
  const queryClient = useQueryClient();
  const getMixpanelProps = useGetMixpanelProps();

  const queryKey = [
    FILE_BUFFER_PREVIEW_QUERY_KEY,
    { nodeId, isCurrentlyActiveNode, version },
  ];

  const query = useQuery<DataRoomFileBufferPreview>({
    queryKey,
    queryFn: async () => {
      const { data } = await api.dataRoom.nodes.getFileBufferPreview(
        nodeId,
        isCurrentlyActiveNode,
      );

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled,
    staleTime: PREVIEW_CACHE_TIME,
    cacheTime: PREVIEW_CACHE_TIME,
  });

  const removeFromCache = useCallback(
    () => queryClient.removeQueries({ queryKey }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, nodeId, isCurrentlyActiveNode, version],
  );

  const updateSoftSignal = useCallback(
    (lastSoftSignal: SoftSignal) =>
      queryClient.setQueryData<DataRoomFileBufferPreview>(queryKey, (prev) =>
        prev ? { ...prev, lastSoftSignal } : prev,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, nodeId, isCurrentlyActiveNode, version],
  );

  // Refetched on the next open instead of now, so the open preview doesn't reload.
  const markStale = useCallback(
    () => queryClient.invalidateQueries({ queryKey, refetchType: 'none' }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, nodeId, isCurrentlyActiveNode, version],
  );

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [FILE_BUFFER_PREVIEW_QUERY_KEY],
      }),
    [queryClient],
  );

  return { query, removeFromCache, updateSoftSignal, markStale, invalidate };
}
